<script setup lang="ts">
import { computed } from 'vue';

import type { GaiaJsonValue, PlaybookExtractionResponseType } from '@/api';
import { formatDisplayValue } from '@/utils/displayAiResponse';
import { getListItemType } from '@/utils/fieldType';
import GaiaTag from '@/components/design/base/GaiaTag.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';

const props = defineProps<{
  value: GaiaJsonValue;
  responseType: PlaybookExtractionResponseType | null;
}>();

const isList = computed<boolean>(
  () => Array.isArray(props.value) || (props.responseType?.startsWith('list[') ?? false)
);

/** List items, each formatted on its own according to the inner item type. */
const listItems = computed<string[]>(() => {
  if (!Array.isArray(props.value)) return [];
  const itemType = props.responseType ? getListItemType(props.responseType) : undefined;
  return props.value.map(item => formatDisplayValue(item, itemType));
});

/** Formatted scalar value (also used as the empty/N-A check for lists). */
const scalarValue = computed<string>(() =>
  formatDisplayValue(props.value, props.responseType ?? undefined)
);

const isEmpty = computed<boolean>(
  () => scalarValue.value === 'N/A' || scalarValue.value === 'Empty'
);
</script>

<template>
  <gaia-text v-if="isEmpty" tone="subtle">No value extracted.</gaia-text>
  <n-flex v-else-if="isList" :size="6">
    <gaia-tag
      v-for="(item, index) in listItems"
      :key="index"
      variant="muted"
      :style="{ maxWidth: '100%', whiteSpace: 'normal', height: 'auto' }"
    >
      {{ item }}
    </gaia-tag>
  </n-flex>
  <gaia-text
    v-else
    weight="semibold"
    tone="ink"
    :style="{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }"
  >
    {{ scalarValue }}
  </gaia-text>
</template>
